import React, { createContext, useContext, useState, useCallback } from 'react'
import { useT } from '../../i18n'

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  type: ToastType
  title: string
  message?: string
  duration?: number
}

interface ToastContextValue {
  toasts: Toast[]
  toast: (opts: Omit<Toast, 'id'>) => void
  success: (title: string, message?: string) => void
  error: (title: string, message?: string) => void
  warning: (title: string, message?: string) => void
  info: (title: string, message?: string) => void
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((t) => t.filter((toast) => toast.id !== id))
  }, [])

  const toast = useCallback((opts: Omit<Toast, 'id'>) => {
    const id = Math.random().toString(36).slice(2)
    const duration = opts.duration ?? (opts.type === 'error' ? 6000 : 3500)
    setToasts((t) => [...t.slice(-4), { ...opts, id, duration }]) // keep max 5
    if (duration > 0) setTimeout(() => dismiss(id), duration)
  }, [dismiss])

  const success = useCallback((title: string, message?: string) => toast({ type: 'success', title, message }), [toast])
  const error   = useCallback((title: string, message?: string) => toast({ type: 'error', title, message }), [toast])
  const warning = useCallback((title: string, message?: string) => toast({ type: 'warning', title, message }), [toast])
  const info    = useCallback((title: string, message?: string) => toast({ type: 'info', title, message }), [toast])

  return (
    <ToastContext.Provider value={{ toasts, toast, success, error, warning, info, dismiss }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

// ─── Toast container ──────────────────────────────────────────────────────────

// Theme variables only: the old fixed colours (near-white text on a translucent
// tint) could not be read on the light theme.
const COLORS: Record<ToastType, { soft: string; icon: string }> = {
  success: { soft: 'var(--ok-soft)',   icon: 'var(--ok)' },
  error:   { soft: 'var(--err-soft)',  icon: 'var(--err)' },
  warning: { soft: 'var(--warn-soft)', icon: 'var(--warn)' },
  info:    { soft: 'var(--info-soft)', icon: 'var(--info)' },
}

const ICONS: Record<ToastType, string> = {
  success: '✓', error: '✕', warning: '⚠', info: 'ℹ',
}

function ToastContainer({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: string) => void }) {
  const t = useT()
  return (
    <>
      <div style={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        maxWidth: 380,
        width: '100%',
        pointerEvents: 'none',
      }} role="region" aria-label={t('common.notifications')}>
        {toasts.map((toast) => {
          const c = COLORS[toast.type]
          return (
            <div
              key={toast.id}
              role={toast.type === 'error' ? 'alert' : 'status'}
              style={{
                background: `linear-gradient(${c.soft}, ${c.soft}), var(--bg-2)`,
                border: `1px solid ${c.icon}`,
                borderRadius: 10,
                padding: '12px 16px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
                pointerEvents: 'all',
                animation: 'toastIn 0.2s ease',
              }}
            >
              <span style={{
                width: 22, height: 22, borderRadius: '50%',
                background: c.soft, color: c.icon,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700, flexShrink: 0,
              }}>
                {ICONS[toast.type]}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--fg)' }}>{toast.title}</div>
                {toast.message && (
                  <div style={{ fontSize: 12, color: 'var(--fg-2)', marginTop: 2 }}>{toast.message}</div>
                )}
              </div>
              <button
                onClick={() => onDismiss(toast.id)}
                aria-label={t('common.dismiss')}
                style={{
                  background: 'none', border: 'none', color: 'var(--fg-3)',
                  cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: '0 2px', flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          )
        })}
      </div>
      <style>{`
        @keyframes toastIn {
          from { opacity: 0; transform: translateY(8px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  )
}
